import { OptionCard } from "@/components/WizardShell";
import { Input } from "@/components/ui/input";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Step2Company({ ref_data, value, onChange }) {
  const { t } = useTranslation();
  const set = (patch) => onChange(patch);
  const selectedSize = ref_data.sizes.find((s) => s.id === value.size);
  const proposedIdentities = selectedSize ? selectedSize.avg_identities : null;

  return (
    <div className="space-y-10 py-8" data-testid="step-2">
      <div>
        <div className="label-caps">{t("wizard.s2_sector")}</div>
        <select
          value={value.sector}
          onChange={(e) => set({ sector: e.target.value })}
          className="mt-3 w-full rounded-sm border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:outline-none focus:border-brand-fuchsia focus:ring-1 focus:ring-brand-fuchsia"
          data-testid="company-sector"
        >
          <option value="">{t("wizard.s2_sector_choose")}</option>
          {ref_data.sectors.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div>
        <div className="label-caps">{t("wizard.s2_size")}</div>
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          {ref_data.sizes.map((s) => (
            <OptionCard key={s.id} selected={value.size === s.id} onClick={() => {
              const patch = { size: s.id };
              if (!value.identities || value.identities === 0) patch.identities = s.avg_identities;
              set(patch);
            }} testId={`company-size-${s.id}`}>
              {s.label} <span className="text-xs text-slate-500 ml-1">{t("wizard.s2_size_hint", { n: s.avg_identities })}</span>
            </OptionCard>
          ))}
        </div>
      </div>

      <div>
        <div className="label-caps flex items-center gap-2">{t("wizard.s2_identities_label")}
          <span className="normal-case tracking-normal text-xs text-slate-400 inline-flex items-center gap-1"><Info className="h-3 w-3" /> {t("wizard.s2_identities_hint")}</span>
        </div>
        <Input
          type="number" min={1}
          value={value.identities}
          onChange={(e) => set({ identities: parseInt(e.target.value || "0", 10) })}
          className="mt-3 max-w-xs"
          data-testid="company-identities"
        />
        {proposedIdentities && (
          <div className="mt-2 text-xs text-slate-500">{t("wizard.s2_identities_est", { n: proposedIdentities })}</div>
        )}
      </div>

      <div>
        <div className="label-caps">{t("wizard.s2_crit")}</div>
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          {ref_data.criticity_levels.map((c) => (
            <OptionCard key={c.id} selected={value.criticality === c.id} onClick={() => set({ criticality: c.id })} testId={`company-crit-${c.id}`}>
              {c.label}
            </OptionCard>
          ))}
        </div>
      </div>

      <div>
        <div className="label-caps">{t("wizard.s2_downtime")}</div>
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          {ref_data.downtime_costs.map((d) => (
            <OptionCard key={d.id} selected={value.hourly_downtime_cost === d.id}
              onClick={() => set({ hourly_downtime_cost: d.id, hourly_downtime_cost_value: d.value })}
              testId={`company-downtime-${d.id}`}>
              {d.label}
            </OptionCard>
          ))}
        </div>
        {value.hourly_downtime_cost === "d6" && (
          <div className="mt-3">
            <div className="text-xs text-slate-500 mb-2">{t("wizard.s2_downtime_hyp")}</div>
            <Input
              type="number" min={0}
              value={value.hourly_downtime_cost_value ?? ""}
              placeholder="5000"
              onChange={(e) => set({ hourly_downtime_cost_value: parseFloat(e.target.value || "0") })}
              className="max-w-xs"
              data-testid="company-downtime-value"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export const step2Valid = (v) => !!(v.sector && v.size && v.identities > 0 && v.criticality && v.hourly_downtime_cost);
